<!-- User Management -->
<div class="user-management-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-main">
        <div class="header-icon">
          <i class="fa-solid fa-users"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">User Management</h1>
          <p class="page-description">Manage system users, roles, and permissions</p>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline-secondary btn-sm" (click)="getUsers()" [disabled]="isLoadingUsers">
          <i class="fa-solid fa-arrows-rotate" [class.fa-spin]="isLoadingUsers"></i>
          Refresh
        </button>
        <button class="btn btn-primary" (click)="createuser()" [disabled]="isLoadingUsers">
          <i class="fa-solid fa-user-plus"></i>
          Add User
        </button>
      </div>
    </div>
  </div>

  <!-- Filters Panel -->
  <div class="filters-panel">
    <div class="search-section">
      <div class="search-box">
        <div class="search-input-wrapper">
          <i class="fa-solid fa-magnifying-glass search-icon" [class.fa-spin]="isSearching"></i>
          <input type="text"
                 class="search-input"
                 placeholder="Search by name, email, or phone..."
                 [(ngModel)]="search"
                 (input)="onSearch()"
                 [disabled]="isLoadingUsers">
          @if (search) {
            <button class="clear-search" (click)="search = ''; onSearch()">
              <i class="fa-solid fa-xmark"></i>
            </button>
          }
        </div>
      </div>
    </div>

    <div class="filters-section">
      <div class="filter-group">
        <!-- Role Filter -->
        <div ngbDropdown class="filter-dropdown">
          <button type="button"
                  class="btn btn-filter"
                  ngbDropdownToggle
                  [disabled]="isLoadingUsers || isFiltering">
            <i class="fa-solid fa-user-tag me-2"></i>
            {{ selectedRole || 'All Roles' }}
            <i class="fa-solid fa-chevron-down ms-2"></i>
          </button>
          <div ngbDropdownMenu class="filter-menu">
            <button ngbDropdownItem (click)="clearFilter()" class="filter-item">
              <i class="fa-solid fa-users me-2"></i>All Roles
            </button>
            <button ngbDropdownItem (click)="Filter('admin')" class="filter-item">
              <i class="fa-solid fa-user-shield me-2"></i>Admin
            </button>
            <button ngbDropdownItem (click)="Filter('waiter')" class="filter-item">
              <i class="fa-solid fa-user-tie me-2"></i>Waiter
            </button>
          </div>
        </div>

        <!-- Status Filter -->
        <div ngbDropdown class="filter-dropdown">
          <button type="button"
                  class="btn btn-filter"
                  ngbDropdownToggle
                  [disabled]="isLoadingUsers || isFiltering">
            <i class="fa-solid fa-circle-half-stroke me-2"></i>
            {{ selectedStatus || 'All Status' }}
            <i class="fa-solid fa-chevron-down ms-2"></i>
          </button>
          <div ngbDropdownMenu class="filter-menu">
            <button ngbDropdownItem (click)="clearFilter()" class="filter-item">
              <i class="fa-solid fa-circle me-2"></i>All Status
            </button>
            <button ngbDropdownItem (click)="statuusFilter('active')" class="filter-item">
              <i class="fa-solid fa-circle-check me-2 text-success"></i>Active
            </button>
            <button ngbDropdownItem (click)="statuusFilter('inactive')" class="filter-item">
              <i class="fa-solid fa-circle-xmark me-2 text-danger"></i>Suspended
            </button>
          </div>
        </div>

        @if (selectedRole || selectedStatus || search) {
          <button class="btn btn-clear-filters"
                  (click)="clearFilter()"
                  [disabled]="isLoadingUsers">
            <i class="fa-solid fa-xmark me-1"></i>
            Clear Filters
          </button>
        }
      </div>
    </div>

    <div class="stats-section">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fa-solid fa-users"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ users?.length || 0 }}</div>
          <div class="stat-label">Showing</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon total">
          <i class="fa-solid fa-database"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ size || 0 }}</div>
          <div class="stat-label">Total Users</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Data Panel -->
  <div class="data-panel">
    <div class="panel-header">
      <div class="panel-title">
        <div class="title-icon">
          <i class="fa-solid fa-table-list"></i>
        </div>
        <div class="title-content">
          <div class="title-text">Users Directory</div>
          <div class="title-subtitle">Complete list of system users with their roles and status</div>
        </div>
      </div>
      @if (!isLoadingUsers) {
        <div class="panel-info">
          <div class="info-item">
            <span class="info-label">Page:</span>
            <span class="info-value">{{ page }} of {{ getTotalPages() }}</span>
          </div>
        </div>
      }
    </div>

    <div class="panel-body">
      @if (isLoadingUsers) {
        <!-- Loading Shimmer State -->
        <div class="loading-state">
          <div class="loading-content">
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
            </div>
            <div class="loading-text">
              <h5>Loading Users</h5>
              <p>Please wait while we fetch user data...</p>
            </div>
          </div>
        </div>
      } @else {
        <div class="table-container">
          <table class="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Contact</th>
                <th>Role</th>
                <th>Status</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for(user of users; track user.id) {
                <tr>
                  <td>
                    <div class="d-flex align-items-center gap-3">
                      <div class="user-avatar-wrap">
                        <img [src]="user?.image || '/public/profile.png'"
                             [alt]="user?.first_name + ' ' + user?.last_name"
                             class="user-avatar-img">
                        <span class="avatar-indicator"
                              [class.indicator-active]="!user?.disabled"
                              [class.indicator-inactive]="user?.disabled"></span>
                      </div>
                      <div>
                        <div class="fw-semibold text-primary-emphasis">{{ user?.first_name }} {{ user?.last_name }}</div>
                        <small class="text-muted">ID: {{ user?.id }}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex flex-column gap-1">
                      <span class="d-flex align-items-center gap-2 small">
                        <i class="fa-solid fa-envelope" style="color: var(--text-tertiary); width: 14px;"></i>
                        {{ user?.email }}
                      </span>
                      <span class="d-flex align-items-center gap-2 small text-muted">
                        <i class="fa-solid fa-phone" style="color: var(--text-tertiary); width: 14px;"></i>
                        {{ user?.phone || 'N/A' }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex flex-wrap gap-1">
                      @for(role of user.roles; track role; let last = $last) {
                        <span class="badge badge-dot"
                              [class.badge-soft-warning]="role === 'admin'"
                              [class.badge-soft-info]="role === 'waiter'">
                          {{ capitalizeFirstLetter(role) }}
                        </span>
                      }
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-dot"
                          [class.badge-status-active]="!user?.disabled"
                          [class.badge-status-suspended]="user?.disabled">
                      {{ !user?.disabled ? 'Active' : 'Suspended' }}
                    </span>
                  </td>
                  <td class="text-end">
                    <div ngbDropdown class="d-inline-block" placement="bottom-end" container="body">
                      <button type="button"
                              class="btn btn-action-icon"
                              ngbDropdownToggle
                              [disabled]="updatingStatusId === user.id"
                              aria-label="User actions"
                              title="More actions">
                        <i class="fa-solid fa-ellipsis-vertical"></i>
                      </button>
                      <div ngbDropdownMenu class="action-menu">
                        <button ngbDropdownItem (click)="edit(user?.id)">
                          <i class="fa-solid fa-pen-to-square me-2"></i>
                          Edit User
                        </button>

                        @if (user?.roles?.includes('waiter')) {
                          <button ngbDropdownItem (click)="viewBalance(user?.id)">
                            <i class="fa-solid fa-wallet me-2"></i>
                            View Balance
                          </button>
                        }

                        <div class="dropdown-divider"></div>

                        <button ngbDropdownItem
                                (click)="updateStatus(user?.id)"
                                [class.text-danger]="!user?.disabled"
                                [class.text-success]="user?.disabled"
                                [disabled]="updatingStatusId === user.id">
                          @if (updatingStatusId === user.id) {
                            <i class="fa-solid fa-spinner fa-spin me-2"></i>
                            Processing...
                          } @else {
                            @if (!user?.disabled) {
                              <i class="fa-solid fa-ban me-2"></i>
                              Suspend User
                            } @else {
                              <i class="fa-solid fa-circle-check me-2"></i>
                              Activate User
                            }
                          }
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="5">
                    <div class="empty-state">
                      @if (search || selectedRole || selectedStatus) {
                        <div class="empty-icon">
                          <i class="fa-solid fa-magnifying-glass fa-3x"></i>
                        </div>
                        <div class="empty-content">
                          <h5>No Users Found</h5>
                          <p>No users match your current search criteria. Try adjusting your filters or search terms.</p>
                          <button class="btn btn-outline-primary btn-sm" (click)="clearFilter()">
                            Clear All Filters
                          </button>
                        </div>
                      } @else {
                        <div class="empty-icon">
                          <i class="fa-solid fa-user-group fa-3x"></i>
                        </div>
                        <div class="empty-content">
                          <h5>No Users Yet</h5>
                          <p>Get started by creating your first user account for the system.</p>
                          <button class="btn btn-primary btn-sm" (click)="createuser()">
                            <i class="fa-solid fa-user-plus me-1"></i>
                            Create First User
                          </button>
                        </div>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>

    <!-- Pagination Footer -->
    @if (size > pageSize && !isLoadingUsers) {
      <div class="panel-footer">
        <div class="pagination-info">
          <span>Showing {{ ((page - 1) * pageSize) + 1 }} to {{ getEndEntry() }} of {{ size }} entries</span>
        </div>
        <ngb-pagination
          [collectionSize]="size"
          [(page)]="page"
          [pageSize]="pageSize"
          [maxSize]="5"
          [rotate]="true"
          [boundaryLinks]="true"
          (pageChange)="refresh($event)">
        </ngb-pagination>
      </div>
    }
  </div>
</div>
